import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import CurrencyDialog, { convert } from '@/components/admin/CurrencyDialog';
import { BOOL_STYLES } from '@/shared/constants';
import { CURRENCY_FIELDS } from '@/shared/fields';
import { committed } from '@/shared/data';
import { money } from '@/shared/pricing';

export const route = { path: '/admin/currencies/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

const SAMPLES = [
  { label: 'Wraith · 1 Month', usd: 7 },
  { label: 'Demon · 1 Month · 1 connection', usd: 4 },
  { label: 'Demon · 1 Year · 2 connections', usd: 56 },
  { label: 'Wraith · 12 Months', usd: 40 },
  { label: 'Demon · 3 Years · 5 connections', usd: 300 },
];

export default function AdminCurrencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: currency, loading, error, run } = useFetch(
    () => sdk.table('currencies_c').select(CURRENCY_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/currencies" label="Back to Currencies" />;
  const gate = recordGate({ back, loading, record: currency, error, run, what: 'Currency' });
  if (gate) return gate;

  async function toggle() {
    const next = !currency.active_c;
    const res = await sdk.table('currencies_c').update({ Id: currency.Id, active_c: next });
    if (!committed(res, next ? `${currency.Name} shown at checkout` : `${currency.Name} hidden at checkout`)) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('currencies_c').remove(currency.Id);
    if (!committed(res, 'Currency deleted')) return false;
    navigate('/admin/currencies');
    return true;
  }

  return (
    <div className="mx-auto max-w-3xl">
      {back}
      <PageHeader
        eyebrow="Currency"
        title={<span className="font-mono">{currency.Name}</span>}
        description={`1 USD = ${Number(currency.rate_c || 0).toFixed(4)} ${currency.Name}`}
        actions={
          <>
            <StatusBadge value={currency.active_c ? 'Shown' : 'Hidden'} styles={{ Shown: BOOL_STYLES.Yes, Hidden: BOOL_STYLES.No }} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete
              title={`Delete ${currency.Name}?`}
              description="Checkout stops showing estimates in it. Past orders are not affected."
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <ConfirmAction
          label={currency.active_c ? 'Hide at checkout' : 'Show at checkout'}
          icon={currency.active_c ? 'EyeOff' : 'Eye'}
          title={currency.active_c ? `Hide ${currency.Name} at checkout?` : `Show ${currency.Name} at checkout?`}
          description="Only the price estimate changes. Everyone is still invoiced in USD."
          busyLabel="Saving…"
          onConfirm={toggle}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Details" icon="Info">
          <dl className="divide-y divide-border">
            <DetailRow label="Code">
              <span className="font-mono">{currency.Name}</span>
            </DetailRow>
            <DetailRow label="Symbol">{currency.symbol_c}</DetailRow>
            <DetailRow label="Rate per USD">{Number(currency.rate_c || 0).toFixed(4)}</DetailRow>
            <DetailRow label="Shown at checkout">{currency.active_c ? 'Yes' : 'No'}</DetailRow>
          </dl>
        </Panel>
        <Panel title="What customers see" icon="Calculator">
          <ul className="divide-y divide-border text-sm">
            {SAMPLES.map((s) => (
              <li key={s.label} className="flex items-center justify-between gap-3 py-2.5">
                <span className="text-muted-foreground">{s.label}</span>
                <span className="tabular-nums">
                  {money(s.usd)} ≈ {convert(s.usd, currency)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <CurrencyDialog open={editOpen} onOpenChange={setEditOpen} currency={currency} onSaved={run} />
    </div>
  );
}
